import { useAbility, useAuth, useProfile } from "@amazing-care/auth";
import { Avatar, AvatarFallback } from "@amazing-care/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@amazing-care/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@amazing-care/ui/sidebar";
import {
  ArrowRight,
  BookOpen,
  Building2,
  ChevronsUpDown,
  FolderCog,
  HeartPulse,
  Home,
  Laptop,
  LogOut,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { Link, useLocation } from "react-router";
import { AmazingCareLogo } from "@/components/amazing-care-logo";
import { useUnreadAnnouncementCount } from "@/features/staff-home/use-staff-home";
import { ROUTES } from "@/routes";
import { useDepartments } from "./use-departments";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const DEPARTMENT_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  it: Laptop,
  hr: Users,
  "clinical-ops": HeartPulse,
  "business-development": TrendingUp,
};

/** Collapsed to an icon, the card would not fit, so the rail gets a plain button. */
function GuideCard({ isActive }: { isActive: boolean }) {
  return (
    <>
      <Link
        to={ROUTES.guide}
        aria-current={isActive ? "page" : undefined}
        className="group/guide block rounded-xl border border-sidebar-border bg-linear-to-br from-primary/10 via-sidebar to-sidebar p-3 transition-colors hover:border-primary/40 aria-[current=page]:border-primary/50 group-data-[collapsible=icon]:hidden"
      >
        <div className="flex items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <BookOpen className="size-4" />
          </span>
          <span className="text-sm font-medium">Portal guide</span>
        </div>
        <p className="mt-2 text-xs leading-snug text-muted-foreground">
          How to upload, replace and look after documents.
        </p>
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary">
          Open the guide
          <ArrowRight className="size-3 transition-transform group-hover/guide:translate-x-0.5" />
        </span>
      </Link>
      <SidebarMenu className="hidden group-data-[collapsible=icon]:flex">
        <SidebarMenuItem>
          <SidebarMenuButton
            render={<Link to={ROUTES.guide} />}
            isActive={isActive}
            tooltip="Portal guide"
          >
            <BookOpen />
            <span>Portal guide</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}

export function StaffSidebar() {
  const location = useLocation();
  const { data: departments, isPending: isDepartmentsPending } = useDepartments();
  const { profile, role } = useProfile();
  const { signOut } = useAuth();
  const ability = useAbility();
  const canManageUsers = ability.can("manage", "Manager");
  const canManageContent = ability.can("manage", "Announcement");
  const unreadCount = useUnreadAnnouncementCount();

  const name = profile?.full_name ?? "";
  const pathname = location.pathname;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <AmazingCareLogo className="h-9 w-auto object-contain group-data-[collapsible=icon]:hidden" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  render={<Link to={ROUTES.staff} />}
                  isActive={pathname === ROUTES.staff}
                  tooltip="Home"
                >
                  <Home />
                  <span>Home</span>
                </SidebarMenuButton>
                {unreadCount > 0 ? <SidebarMenuBadge>{unreadCount}</SidebarMenuBadge> : null}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Departments</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {isDepartmentsPending
                ? [0, 1, 2, 3].map((slot) => (
                    <SidebarMenuItem key={slot}>
                      <SidebarMenuSkeleton showIcon />
                    </SidebarMenuItem>
                  ))
                : null}
              {departments?.map((department) => {
                const href = ROUTES.department(department.id);
                const Icon = DEPARTMENT_ICONS[department.id] ?? Building2;
                return (
                  <SidebarMenuItem key={department.id}>
                    <SidebarMenuButton
                      render={<Link to={href} />}
                      isActive={pathname.startsWith(href)}
                      tooltip={department.name}
                    >
                      <Icon />
                      <span>{department.name}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {canManageUsers || canManageContent ? (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {canManageUsers ? (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      render={<Link to={ROUTES.adminUsers} />}
                      isActive={pathname.startsWith(ROUTES.adminUsers)}
                      tooltip="User management"
                    >
                      <UserCog />
                      <span>User management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : null}
                {canManageContent ? (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      render={<Link to={ROUTES.adminContent} />}
                      isActive={pathname.startsWith(ROUTES.adminContent)}
                      tooltip="Content management"
                    >
                      <FolderCog />
                      <span>Content management</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : null}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      <SidebarFooter>
        <GuideCard isActive={pathname.startsWith(ROUTES.guide)} />
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8">
                      <AvatarFallback>{getInitials(name)}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left leading-tight">
                      <span className="truncate text-sm font-medium">{name}</span>
                      <span className="truncate text-xs text-muted-foreground">{role}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
                  </SidebarMenuButton>
                }
              />
              <DropdownMenuContent className="w-(--anchor-width) min-w-56" side="top" align="end">
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5">
                      <Avatar className="size-8">
                        <AvatarFallback>{getInitials(name)}</AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left leading-tight">
                        <span className="truncate text-sm font-medium">{name}</span>
                        <span className="truncate text-xs text-muted-foreground">{role}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={() => signOut()}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
